import { useEffect, useRef, useState } from "react";

import { Slider, type SliderRef } from "@/components/ui/slider";

import { Progress } from "../../progress";
import "./timeline-carousel.css";

type DataType = {
  title: string;
  description: string;
  image: string;
};

export const TimelineCarousel = ({ slides }: { slides: DataType[] }) => {
  const sliderRef = useRef<SliderRef>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState<"next" | "prev" | "reset">("next");
  const getDirection = (oldIndex: number, newIndex: number, total: number) => {
    if (oldIndex === total - 1 && newIndex === 0) return "reset";
    if (oldIndex === 0 && newIndex === total - 1) return "reset";
    if (newIndex === oldIndex + 1) return "next";
    if (newIndex === oldIndex - 1) return "prev";
    return "next";
  };
  if (!slides) return null;
  return (
    <Slider
      ref={sliderRef}
      className="timeline-carousel rounded-3xl overflow-hidden"
      infinite={true}
      speed={500}
      slidesToShow={1}
      slidesToScroll={1}
      arrows={false}
      autoplay={false}
      pauseOnDotsHover={true}
      dots={true}
      appendDots={(dots) => (
        <ul className="grid grid-cols-3 gap-2 items-center justify-center">
          {dots}
        </ul>
      )}
      customPaging={(i) => {
        return (
          <TimelineCarouselProgress
            key={i}
            index={i}
            activeSlide={activeSlide}
            direction={direction}
            onNext={() => {
              sliderRef?.current?.slickNext?.();
            }}
          />
        );
      }}
      beforeChange={(oldIndex, newIndex) => {
        setDirection(getDirection(oldIndex, newIndex, slides.length));
      }}
      afterChange={(current) => {
        setActiveSlide(current);
      }}
    >
      {slides.map((slide) => (
        <TimelineCarouselItem key={slide.title} slide={slide} />
      ))}
    </Slider>
  );
};

const TimelineCarouselItem = ({ slide }: { slide: DataType }) => {
  return (
    <div
      className="rounded-3xl relative h-full bg-cover bg-center flex flex-col justify-end overflow-hidden cursor-grab active:cursor-grabbing"
      style={{ backgroundImage: `url(${slide.image})` }}
    >
      <div className="px-12 pt-31 pb-19.5 backdrop-blur-[1px] bg-slider-gradient-bottom">
        <h3 className="text-4xl font-medium text-text-light">{slide.title}</h3>
        <p className="text-xl text-text-light mt-3">{slide.description}</p>
      </div>
    </div>
  );
};

const TimelineCarouselProgress = ({
  index,
  activeSlide,
  onNext,
  direction,
}: {
  index: number;
  activeSlide: number;
  onNext: () => void;
  direction: "next" | "prev" | "reset";
}) => {
  const [value, setValue] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!direction) return;
    switch (direction) {
      case "reset":
        setValue(0);
        break;

      case "next":
        if (activeSlide > index) setValue(100);
        break;

      case "prev":
        if (activeSlide <= index) setValue(0);
        break;
    }
  }, [direction, activeSlide]);

  useEffect(() => {
    if (activeSlide !== index || isPaused) {
      return;
    }
    const interval = setInterval(() => {
      setValue((prev) => {
        if (prev >= 100) {
          onNext?.();
          clearInterval(interval);
        }
        return prev + 1;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [activeSlide, index, isPaused]);

  const handleMouseEnter = () => {
    setIsPaused(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Progress value={value} max={100} />
    </div>
  );
};
